import { useState, useEffect } from 'react';
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from './InterviewProfileForm.module.scss';
import { value } from 'jsonpath';

export function InterviewProfileForm({ props }) {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        experience: '',
        currentRole: '',
        company: '',
        projects: [{ title: '', description: '', technologies: '', duration: '' }],
        skills: [{ name: '', level: '', yearsOfExperience: '' }],
    });

    const [showWarning, setShowWarning] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const initializeProfile = async () => {
            try {
                // 1. Check localStorage first
                const localProfile = localStorage.getItem('profileData');
                if (localProfile) {
                    const parsedProfile = JSON.parse(localProfile);
                    // If profile exists in localStorage, navigate to practice
                    navigate('/interview/practice');
                    return;
                }

                // 2. If not in localStorage, check database
                const response = await axios.get('/api/interview/user/projects');
                if (response.data) {
                    // Store in localStorage and navigate
                    localStorage.setItem('profileData', JSON.stringify(response.data));
                    navigate('/interview/practice');
                    return;
                }

                // 3. If no profile found anywhere, show the form
                setIsLoading(false);
            } catch (error) {
                console.error('Error initializing profile:', error);
                setIsLoading(false);
            }
        };

        initializeProfile();
    }, [navigate]);

    const formatDataForStorage = () => ({
        current_role: formData.currentRole,
        company: formData.company,
        experience: formData.experience,
        projects: formData.projects.map((project) => ({
            desc: project.description,
            name: project.title,
            duration: project.duration,
            'tech-used': project.technologies,
        })),
        skills: formData.skills.map((skill) => ({
            exp: skill.yearsOfExperience,
            name: skill.name,
            level: skill.level,
        })),
    });

    const isFormValid = () =>
        Boolean(formData.currentRole &&
            formData.experience
            &&
            formData.projects.every((p) => p.title && p.description && p.technologies && p.duration) &&
            formData.skills.every((s) => s.name && s.level && s.yearsOfExperience))

    const handleSubmit = async () => {
        if (!isFormValid()) {
            setShowWarning(true);
            return;
        }
        setShowWarning(false);

        try {
            const formattedData = formatDataForStorage();

            // Store in localStorage
            localStorage.setItem('profileData', JSON.stringify(formattedData));

            // Store in database
            await axios.post('/api/interview/user/projects', formattedData);

            // Navigate to practice page
            navigate('/interview/practice');
        } catch (error) {
            console.error('Error submitting profile:', error);
            navigate('/oops');
        }
    };

    const addProject = () => {
        setFormData((prev) => ({
            ...prev,
            projects: [...prev.projects, { title: '', description: '', technologies: '', duration: '' }],
        }));
    };

    const removeProject = (index) => {
        setFormData((prev) => ({
            ...prev,
            projects: prev.projects.filter((_, i) => i !== index),
        }));
    };

    const addSkill = () => {
        setFormData((prev) => ({
            ...prev,
            skills: [...prev.skills, { name: '', level: '', yearsOfExperience: '' }],
        }));
    };

    const removeSkill = (index) => {
        setFormData((prev) => ({
            ...prev,
            skills: prev.skills.filter((_, i) => i !== index),
        }));
    };

    const updateProject = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            projects: prev.projects.map((project, i) => (i === index ? { ...project, [field]: value } : project)),
        }));
    };

    const updateSkill = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            skills: prev.skills.map((skill, i) => (i === index ? { ...skill, [field]: value } : skill)),
        }));
    };

    if (isLoading) {
        return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
    }

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.formContainer}>
                <div className={styles.formHeader}>
                    <h1>Professional Experience</h1>
                    <p>Tell us about your experience and skills</p>
                </div>

                {/* Work Experience Section */}
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <FeatherIcon size="0.9em" icon="briefcase" />
                        <h2>Work Experience</h2>
                    </div>

                    <div className={styles.formGroup}>
                        <label className="block mb-1">
                            Current Role <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.currentRole}
                            onChange={(e) => setFormData((prev) => ({ ...prev, currentRole: e.target.value }))}
                            placeholder="e.g. Senior Software Engineer"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Company/Institute</label>
                        <input
                            type="text"
                            value={formData.company}
                            onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                            placeholder="e.g. Google"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>
                            Total Years of Experience <span className={styles.required}>*</span>
                        </label>
                        <select
                            value={formData.experience}
                            onChange={(e) => setFormData((prev) => ({ ...prev, experience: e.target.value }))}
                        >
                            <option value="">Select Experience</option>
                            {[...Array(21)].map((_, i) => (
                                <option key={i} value={i}>
                                    {i} years
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Projects Section */}
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <FeatherIcon size="0.9em" icon="code" />
                        <h2>
                            Projects<span className={styles.required}>*</span>
                        </h2>
                    </div>

                    {formData.projects.map((project, index) => (
                        <div key={index} className={styles.projectCard}>
                            <div className={styles.cardHeader}>
                                <h3>Project {index + 1}</h3>
                                {index > 0 && (
                                    <button className={styles.iconButton} onClick={() => removeProject(index)}>
                                        <FeatherIcon size="0.9em" icon="trash-2" />
                                    </button>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <input
                                    type="text"
                                    value={project.title}
                                    onChange={(e) => updateProject(index, 'title', e.target.value)}
                                    placeholder="Project Title"
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <textarea
                                    value={project.description}
                                    onChange={(e) => updateProject(index, 'description', e.target.value)}
                                    placeholder="Project Description"
                                />
                            </div>

                            <div className={styles.rowGroup}>
                                <div className={styles.formGroup}>
                                    <input
                                        type="text"
                                        value={project.technologies}
                                        onChange={(e) => updateProject(index, 'technologies', e.target.value)}
                                        placeholder="Technologies Used"
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <input
                                        type="text"
                                        value={project.duration}
                                        onChange={(e) => updateProject(index, 'duration', e.target.value)}
                                        placeholder="Duration"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                    <button className={styles.addButton} onClick={addProject}>
                        <FeatherIcon size="0.9em" icon="plus-circle" />
                        Add Another Project
                    </button>
                </div>

                {/* Skills Section */}
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <FeatherIcon size="0.9em" icon="award" />
                        <h2>
                            Skills<span className={styles.required}>*</span>
                        </h2>
                    </div>

                    {formData.skills.map((skill, index) => (
                        <div key={index} className={styles.skillCard}>
                            <div className={styles.rowGroup}>
                                <div className={styles.formGroup}>
                                    <input
                                        type="text"
                                        value={skill.name}
                                        onChange={(e) => updateSkill(index, 'name', e.target.value)}
                                        placeholder="Skill Name"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <select value={skill.level} onChange={(e) => updateSkill(index, 'level', e.target.value)}>
                                        <option value="">Proficiency Level</option>
                                        <option value="beginner">Beginner</option>
                                        <option value="intermediate">Intermediate</option>
                                        <option value="advanced">Advanced</option>
                                        <option value="expert">Expert</option>
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <input
                                        type="number"
                                        value={skill.yearsOfExperience}
                                        onChange={(e) => updateSkill(index, 'yearsOfExperience', e.target.value)}
                                        placeholder="Years of Experience"
                                    />
                                </div>

                                {index > 0 && (
                                    <div className={styles.deleteIconContainer}>
                                        <button className={styles.iconButton} onClick={() => removeSkill(index)}>
                                            <FeatherIcon size="0.9em" icon="trash-2" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    <button className={styles.addButton} onClick={addSkill}>
                        <FeatherIcon size="0.9em" icon="plus-circle" />
                        Add Another Skill
                    </button>
                </div>

                {showWarning && (
                    <div className={styles.warning}>
                        <p>Please fill all mandatory fields marked with. <span className={styles.required}>*</span></p>
                    </div>
                )}

                <div className={styles.formActions}>
                    <button className={styles.nextButton} onClick={handleSubmit} disabled={!isFormValid()}>
                        Continue
                        <FeatherIcon size="0.9em" icon="chevron-right" />
                    </button>
                </div>
            </div>
        </div>
    );
}
